import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const STORAGE_KEY = "bpr_cookie_consent";

const loadAnalytics = (consent) => {
  const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  const gtmId = process.env.REACT_APP_GTM_ID;
  if (consent.statistics && gaId && !document.getElementById("bpr-ga")) {
    const s = document.createElement("script");
    s.id = "bpr-ga";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", gaId, { anonymize_ip: true });
  }
  if (consent.marketing && gtmId && !document.getElementById("bpr-gtm")) {
    const s = document.createElement("script");
    s.id = "bpr-gtm";
    s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
    document.head.appendChild(s);
  }
};

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [settings, setSettings] = useState(false);
  const [prefs, setPrefs] = useState({ statistics: false, marketing: false });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      loadAnalytics(JSON.parse(saved));
    } else {
      setVisible(true);
    }
    const openSettings = () => {
      const current = localStorage.getItem(STORAGE_KEY);
      if (current) setPrefs(JSON.parse(current));
      setSettings(true);
      setVisible(true);
    };
    window.addEventListener("bpr-open-cookie-settings", openSettings);
    return () => window.removeEventListener("bpr-open-cookie-settings", openSettings);
  }, []);

  const save = useCallback((consent) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ necessary: true, ...consent }));
    loadAnalytics(consent);
    setVisible(false);
    setSettings(false);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-20 left-4 right-4 z-[70] max-w-md rounded-2xl border border-[#2B9FD7]/25 bg-[#10232F]/95 p-6 shadow-2xl backdrop-blur-xl sm:left-6 lg:bottom-6"
          role="dialog"
          aria-label="Cookievoorkeuren"
          data-testid="cookie-banner"
        >
          <div className="mb-3 flex items-center gap-2.5">
            <Cookie size={18} className="text-[#62C3F0]" />
            <p className="font-heading text-sm font-bold text-[#F5F7F9]">Cookies op deze website</p>
          </div>
          <p className="text-xs leading-relaxed text-[#CBD3D9]/85">
            Wij gebruiken noodzakelijke cookies voor de werking van de website. Statistiek- en
            marketingcookies plaatsen we alleen met uw toestemming. Lees meer in onze{" "}
            <Link to="/cookieverklaring" className="text-[#62C3F0] underline underline-offset-2">
              cookieverklaring
            </Link>.
          </p>
          {settings && (
            <div className="mt-4 grid gap-3 rounded-xl border border-white/10 bg-[#090D10]/60 p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold text-[#F5F7F9]">Noodzakelijk</p>
                  <p className="text-[11px] text-[#CBD3D9]/60">Altijd actief</p>
                </div>
                <Switch checked disabled aria-label="Noodzakelijke cookies" />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold text-[#F5F7F9]">Statistiek</p>
                  <p className="text-[11px] text-[#CBD3D9]/60">Anonieme bezoekersstatistieken</p>
                </div>
                <Switch
                  checked={prefs.statistics}
                  onCheckedChange={(v) => setPrefs((p) => ({ ...p, statistics: v }))}
                  aria-label="Statistiekcookies"
                  data-testid="cookie-switch-statistics"
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold text-[#F5F7F9]">Marketing</p>
                  <p className="text-[11px] text-[#CBD3D9]/60">Campagnemeting en advertenties</p>
                </div>
                <Switch
                  checked={prefs.marketing}
                  onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                  aria-label="Marketingcookies"
                  data-testid="cookie-switch-marketing"
                />
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => save({ statistics: true, marketing: true })}
              className="rounded-full bg-[#2B9FD7] px-4 py-2 font-heading text-xs font-semibold text-[#090D10] transition-colors hover:bg-[#62C3F0]"
              data-testid="cookie-accept-all"
            >
              Alles accepteren
            </button>
            <button
              type="button"
              onClick={() => save({ statistics: false, marketing: false })}
              className="rounded-full border border-white/20 px-4 py-2 font-heading text-xs font-semibold text-[#F5F7F9] transition-colors hover:border-[#62C3F0]/50"
              data-testid="cookie-necessary-only"
            >
              Alleen noodzakelijk
            </button>
            {settings ? (
              <button
                type="button"
                onClick={() => save(prefs)}
                className="rounded-full border border-[#62C3F0]/50 px-4 py-2 font-heading text-xs font-semibold text-[#62C3F0]"
                data-testid="cookie-save-prefs"
              >
                Voorkeuren opslaan
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setSettings(true)}
                className="px-2 py-2 text-xs font-semibold text-[#CBD3D9] underline underline-offset-2 transition-colors hover:text-[#62C3F0]"
                data-testid="cookie-open-settings"
              >
                Voorkeuren
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
